import React, { useEffect, useState } from "react";
import {
  TextField,
  FunctionField,
  TabbedShowLayout,
  Tab,
  ArrayField,
  Datagrid,
  BooleanField,
  SelectField,
  NumberField,
  DateField,
  TabbedShowLayoutProps,
  Record,
} from "react-admin";
import { useAppData } from "../../../common/hooks/useAppData";
import {
  CompanyDto,
  DutyCycleDto,
  CountryDto,
  StateDto,
  DocumentDto,
} from "../../../common/data/service-proxies";
import {
  Typography,
  Box,
  Theme,
  useTheme,
  IconButton,
} from "@material-ui/core";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { useTranslate } from "react-admin";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";

interface IViewProps<T> {
  record?: T;
}

export const CompanyViewFields = (props: IViewProps<CompanyDto>) => {
  const { record } = props;
  const translate = useTranslate();
  const timeZones = useAppData((x) => x.timeZones);
  const countries = useAppData((x) => x.countries);
  const cargoTypeValues = useAppData((x) => x.cargoTypes) ?? [];
  const cargoTypes = cargoTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.cargoTypes.${x}`),
  }));
  const odometerTypeValues = useAppData((x) => x.odometerTypes) ?? [];
  const odometerTypes = odometerTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.odometerTypes.${x}`),
  }));
  const documentTypes =
    useAppData(
      (x) => x.knownDocumentTypes && x.knownDocumentTypes["Company"]
    ) ?? [];

  const theme: Theme = useTheme();
  const [country, setCountry] = useState<CountryDto>();
  const [state, setState] = useState<StateDto>();
  const [tzName, setTzName] = useState<string>();
  const [dutyCycles, setDutyCycles] = useState<{
    [id: number]: DutyCycleDto;
  }>();

  const IconButtonWrapper = (props: any) => (
    <IconButton {...props}>{props.children}</IconButton>
  );

  useEffect(() => {
    if (!record) {
      return;
    }

    const country = countries?.find((x) => x.id === record.countryId);
    if (country) {
      setCountry({ name: country.name });
      const state = country.states?.find((x) => x.id === record.stateId);
      if (state) {
        setState({ name: state.name });
      }
    }

    const timeZone = timeZones?.find(
      (x) => x.id === record.homeTerminalTimeZone
    );
    if (timeZone) {
      setTzName(timeZone.name!);
    }

    const _dutyCycles: {
      [id: number]: DutyCycleDto;
    } = {};
    record.cycleRules?.forEach((cycleRule) => {
      const cycleRuleCountry = countries?.find(
        (x) => x.id === cycleRule.drivingCountryId
      );
      if (cycleRuleCountry) {
        const selectedCycleRule = cycleRuleCountry.dutyCycles?.find(
          (x) => x.id === cycleRule.cycleRuleId
        );
        if (selectedCycleRule) {
          const dutyCycle = {
            id: selectedCycleRule.id!,
            country: { name: cycleRuleCountry.name },
            name: selectedCycleRule.name,
          };
          _dutyCycles[dutyCycle.id] = dutyCycle;
        }
      }
    });
    setDutyCycles(_dutyCycles);
  }, [countries, timeZones, record]);

  if (!record) {
    return null;
  }

  return (
    <TabbedShowLayout {...(props as TabbedShowLayoutProps)}>
      <Tab label="resources.admin-companies.labels.companyDetails">
        <ProtectedElement source="name">
          <TextField
            source="name"
            label="resources.admin-companies.labels.name"
          />
        </ProtectedElement>
        <ProtectedElement source="street">
          <TextField
            source="street"
            label="resources.admin-companies.labels.street"
          />
        </ProtectedElement>
        <ProtectedElement source="city">
          <TextField
            source="city"
            label="resources.admin-companies.labels.city"
          />
        </ProtectedElement>
        <ProtectedElement source="countryId">
          <FunctionField<CompanyDto & Record>
            source="country.name"
            label="resources.common.labels.country"
            render={() => (
              <Typography variant="body2">{country?.name}</Typography>
            )}
          />
        </ProtectedElement>
        <ProtectedElement source="stateId">
          <FunctionField<CompanyDto & Record>
            source="state.name"
            label="resources.common.labels.state"
            render={() => (
              <Typography variant="body2">{state?.name}</Typography>
            )}
          />
        </ProtectedElement>
        <ProtectedElement source="postalCode">
          <TextField
            source="postalCode"
            label="resources.admin-companies.labels.postalCode"
          />
        </ProtectedElement>
        <ProtectedElement source="dot">
          <TextField
            source="dot"
            label="resources.admin-companies.labels.dot"
          />
        </ProtectedElement>
        <ProtectedElement source="phoneNumber">
          <TextField
            source="phoneNumber"
            label="resources.common.labels.phone"
          />
        </ProtectedElement>
        <ProtectedElement source="email">
          <TextField source="email" label="resources.common.labels.email" />
        </ProtectedElement>
        <FunctionField<CompanyDto & Record>
          source="status"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color: theme.palette.error.main,
                  }}
                />
              )}
            </Box>
          )}
        />
      </Tab>
      <Tab label="resources.admin-companies.labels.hosDefaultSettings">
        <ProtectedElement source="homeTerminalTimeZone">
          <FunctionField<CompanyDto & Record>
            source="homeTerminalTimeZone"
            label="resources.admin-companies.labels.homeTerminalTimeZone"
            render={(record) => (
              <>
                <Typography variant="body2">
                  {record?.homeTerminalTimeZone}
                </Typography>
                <Typography variant="caption">{tzName}</Typography>
              </>
            )}
          />
        </ProtectedElement>
        <ProtectedElement source="cycleRules">
          <ArrayField
            source="cycleRules"
            label="resources.admin-companies.labels.cycleRules">
            <Datagrid>
              <FunctionField<CompanyDto & Record>
                label="resources.common.labels.country"
                render={(record) => (
                  <Typography variant="body2">
                    {record?.cycleRuleId &&
                      dutyCycles &&
                      dutyCycles[record.cycleRuleId]?.country?.name}
                  </Typography>
                )}
              />
              <FunctionField<CompanyDto & Record>
                label="resources.common.labels.cycleRule"
                render={(record) => (
                  <Typography variant="body2">
                    {record?.cycleRuleId &&
                      dutyCycles &&
                      dutyCycles[record.cycleRuleId]?.name}
                  </Typography>
                )}
              />
            </Datagrid>
          </ArrayField>
        </ProtectedElement>
        <ProtectedElement source="cargoType">
          <SelectField
            source="cargoType"
            label="resources.admin-companies.labels.cargoType"
            choices={cargoTypes}
          />
        </ProtectedElement>
        <ProtectedElement source="thirtyMinuteBreakException">
          <BooleanField
            source="thirtyMinuteBreakException"
            label="resources.admin-companies.labels.thirtyMinuteBreakException"
          />
        </ProtectedElement>
        <ProtectedElement source="twentyFourHrCycleReset">
          <BooleanField
            source="twentyFourHrCycleReset"
            label="resources.admin-companies.labels.twentyFourHrCycleReset"
          />
        </ProtectedElement>
        <ProtectedElement source="dvirWarnings">
          <BooleanField
            source="dvirWarnings"
            label="resources.admin-companies.labels.dvirWarnings"
          />
        </ProtectedElement>
        <ProtectedElement source="odometer">
          <SelectField
            source="odometer"
            label="resources.admin-companies.labels.odometer"
            choices={odometerTypes}
          />
        </ProtectedElement>
      </Tab>
      <Tab label="resources.admin-companies.labels.otherSettings">
        <NumberField
          source="fleetOverSpeedingThreshold"
          label="resources.admin-companies.labels.fleetOverSpeedingThreshold"
        />
      </Tab>
      <Tab label="resources.common.labels.documents">
        <ProtectedElement source="documents">
          <ArrayField source="documents" label="">
            <Datagrid>
              <SelectField
                source="documentNameId"
                choices={[...documentTypes, { id: -1, name: "Custom" }]}
                label="resources.common.labels.documentName"
              />
              <TextField
                source="name"
                label="resources.common.labels.customDocumentName"
                emptyText="N/A"
              />
              <FunctionField<DocumentDto & Record>
                source="expirationDate"
                label="resources.common.labels.expirationDate"
                render={(record) => (
                  <Box display="flex" flexDirection="row" alignContent="center">
                    <DateField
                      source="expirationDate"
                      emptyText="N/A"
                      record={record}
                      style={{
                        color:
                          record?.expirationDate &&
                          new Date(record.expirationDate) < new Date()
                            ? theme.palette.error.main
                            : theme.palette.text.primary,
                      }}
                    />
                    {record?.expirationDate &&
                      new Date(record.expirationDate) < new Date() && (
                        <PriorityHighIcon
                          fontSize="small"
                          style={{ color: theme.palette.error.main }}
                        />
                      )}
                  </Box>
                )}
              />

              <TextField
                source="fileName"
                label="resources.common.labels.fileName"
              />
              <FunctionField<DocumentDto & Record>
                source="url"
                label="resources.common.labels.downloadLink"
                render={(record) => (
                  <IconButtonWrapper
                    download
                    href={record?.url}
                    style={{ color: theme.palette.info.main }}>
                    <CloudDownloadIcon />
                  </IconButtonWrapper>
                )}
              />
            </Datagrid>
          </ArrayField>
        </ProtectedElement>
      </Tab>
    </TabbedShowLayout>
  );
};
